// src/components/Cheque/ChequeModal.jsx
// Modal component for adding/editing cheques

import React, { useState, useEffect } from 'react';

/**
 * ChequeModal Component
 * Modal dialog for creating and editing cheque records
 * 
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {Function} onClose - Callback when modal is closed
 * @param {Function} onSave - Callback when form is submitted
 * @param {Object} cheque - Cheque object to edit (null for new cheque)
 * @param {boolean} isSaving - Whether a save operation is in progress
 */
const ChequeModal = ({ isOpen, onClose, onSave, cheque, isSaving }) => {
  // Initial form state
  const initialFormData = {
    name: '',
    amount: '',
    status: 'pending',
    date: ''
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});

  /**
   * Update form data when cheque prop changes
   * This handles switching between add and edit mode
   */
  useEffect(() => {
    if (cheque) {
      // Edit mode - populate with existing data
      setFormData({
        name: cheque.name || '',
        amount: cheque.amount || '',
        status: cheque.status || 'pending',
        date: cheque.date ? new Date(cheque.date).toISOString().split('T')[0] : ''
      });
    } else {
      // Add mode - use empty form
      setFormData(initialFormData);
    }
    // Clear any previous errors
    setErrors({});
  }, [cheque, isOpen]);

  /**
   * Handle input field changes
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  /**
   * Validate form data
   * @returns {boolean} - True if valid, false otherwise
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.amount) {
      newErrors.amount = 'Amount is required';
    } else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be a positive number';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (!isSaving) {
      setFormData(initialFormData);
      setErrors({});
      onClose();
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="cheque-modal-overlay">
      <div className="cheque-modal-content">
        {/* Modal Header */}
        <div className="cheque-modal-header">
          <h2 className="cheque-modal-title">
            {cheque ? 'Edit Cheque' : 'Add New Cheque'}
          </h2>
          <button 
            className="cheque-close-modal-btn" 
            onClick={handleClose}
            disabled={isSaving}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className="cheque-modal-body">
          <form onSubmit={handleSubmit} id="cheque-form">
            <div className="cheque-form-grid">
              {/* Name Field */}
              <label htmlFor="name">Name:</label>
              <div>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`cheque-custom-input ${errors.name ? 'error' : ''}`}
                  disabled={isSaving}
                  placeholder="Enter cheque name"
                />
                {errors.name && (
                  <span className="cheque-error-message">{errors.name}</span>
                )}
              </div>

              {/* Amount Field */}
              <label htmlFor="amount">Amount:</label>
              <div>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  className={`cheque-custom-input ${errors.amount ? 'error' : ''}`}
                  disabled={isSaving}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
                {errors.amount && (
                  <span className="cheque-error-message">{errors.amount}</span>
                )}
              </div>

              {/* Status Field */}
              <label htmlFor="status">Status:</label>
              <div>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="cheque-custom-select"
                  disabled={isSaving}
                  style={{ width: '100%' }}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              {/* Date Field */}
              <label htmlFor="date">Date:</label>
              <div>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className={`cheque-custom-input ${errors.date ? 'error' : ''}`}
                  disabled={isSaving}
                />
                {errors.date && (
                  <span className="cheque-error-message">{errors.date}</span>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Modal Footer */}
        <div className="cheque-modal-footer">
          <button 
            type="button"
            className="cheque-btn cheque-btn-cancel" 
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button 
            type="submit"
            form="cheque-form"
            className="cheque-btn cheque-btn-save"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : (cheque ? 'Update' : 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChequeModal;